import { HttpClient } from '@angular/common/http';
import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { OperatorConfiguration } from '../operator-configuration/operator-configuration.model';
import { TenantService } from '../tenant/tenant.service';
import { applyOperatorTheme, OperatorTheme } from './operator-theme';

@Injectable({ providedIn: 'root' })
export class OperatorThemeService {
  private readonly http = inject(HttpClient);
  private readonly tenantService = inject(TenantService);

  private readonly themeState = signal<OperatorTheme | null>(null);

  readonly theme = this.themeState.asReadonly();

  async loadForTenant(): Promise<void> {
    const domain = this.tenantService.operator()?.domain;

    if (!domain) {
      return;
    }

    try {
      const configuration = await firstValueFrom(
        this.http.get<OperatorConfiguration>(
          `${environment.apiUrl}/operator-configurations/by-domain/${encodeURIComponent(domain)}`,
        ),
      );

      this.applyConfiguration(configuration);
    } catch {
      // Keep default theme when configuration cannot be loaded.
    }
  }

  applyConfiguration(configuration: OperatorConfiguration): void {
    const theme: OperatorTheme = {
      primaryColor: configuration.primaryColor,
      secondaryColor: configuration.secondaryColor,
      accentColor: configuration.accentColor,
      backgroundColor: configuration.backgroundColor,
      uiRadiusType: configuration.uiRadiusType,
      headingFont: configuration.headingFont,
      bodyFont: configuration.bodyFont,
      primaryLogoUrl: configuration.primaryLogoUrl,
      logoDarkBgUrl: configuration.logoDarkBgUrl,
      faviconUrl: configuration.faviconUrl,
      heroBannerUrl: configuration.heroBannerUrl,
    };

    this.themeState.set(theme);
    applyOperatorTheme(theme);
  }
}
